import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Interface,
  ZeroAddress,
  formatEther,
  formatUnits,
  getAddress,
  isAddress,
  parseEther,
  parseUnits,
} from "ethers";
import {
  createSafeDraft,
  createProposal,
  fetchPendingProposals,
  fetchSafes,
  registerSafe,
  reportExecution,
  submitSignature,
  type ProposalInput,
  type SafeRegistrationMetadata,
} from "./api";
import { AddressView, EmptyState, Field, HashView, SpinnerLabel, TxView } from "./components";
import { loadConfig, missingCreationContracts } from "./config";
import {
  ERC20_ABI,
  OWNER_MANAGE_PURPOSE,
  SAFE_TX_TYPES,
  assertSafeTransactionHash,
  createOwnerAccessProof,
  createSafeProxy,
  encodeErc20Transfer,
  encodeSafeSetup,
  executeProposal,
  getSafeSnapshot,
  parseSaltNonce,
  predictSafeAddress,
  proposalTransaction,
  readProvider,
  normaliseSafeSignature,
  ownerAccessProofIsFresh,
  recoverSafeSigner,
  safeDomain,
  tokenMetadata,
  uniqueValidSignatures,
} from "./safe";
import type {
  AppConfig,
  OwnerAccessProof,
  Proposal,
  SafeRecord,
  SafeSnapshot,
  WalletSession,
} from "./types";
import {
  clearLastConnector,
  connectInjected,
  connectWalletConnect,
  hasInjectedWallet,
  rememberConnector,
  refreshSession,
  restoreLastWallet,
  switchOrAddL1X,
} from "./wallet";

type View = "connect" | "create" | "dashboard";
type ProposalType = "transfer" | "threshold";

const CHANGE_THRESHOLD_INTERFACE = new Interface([
  "function changeThreshold(uint256)",
]);
const CHANGE_THRESHOLD_SELECTOR = "0x694e80c3";

interface CreatedResult {
  address: string;
  owners: string[];
  threshold: number;
  transactionHash: string;
}

function errorText(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  // EIP-1193 providers (Rabby, WalletConnect, others) throw plain objects, not Error
  // instances. Surface their message and code instead of a generic string.
  if (error && typeof error === "object") {
    const e = error as { message?: unknown; code?: unknown; reason?: unknown };
    const message =
      (typeof e.message === "string" && e.message) ||
      (typeof e.reason === "string" && e.reason) ||
      "";
    if (message) {
      return typeof e.code === "number" ? `${message} (code ${e.code})` : message;
    }
  }
  if (typeof error === "string" && error.trim()) return error;
  return "Something went wrong.";
}

function savedTokens(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem("l1x-safe-tokens") ?? "[]") as unknown;
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function savedSafe(): string {
  const query = new URLSearchParams(window.location.search).get("safe");
  return query ?? "";
}

function numberLabel(value: bigint, decimals = 18, precision = 6): string {
  const formatted = formatUnits(value, decimals);
  const [whole, fraction = ""] = formatted.split(".");
  const trimmed = fraction.slice(0, precision).replace(/0+$/, "");
  return trimmed ? `${whole}.${trimmed}` : whole;
}

function decodeThresholdChange(proposal: Proposal, safeAddress: string): bigint | null {
  if (
    proposal.to.toLowerCase() !== safeAddress.toLowerCase() ||
    proposal.data.slice(0, 10).toLowerCase() !== CHANGE_THRESHOLD_SELECTOR
  ) {
    return null;
  }
  try {
    const decoded = CHANGE_THRESHOLD_INTERFACE.decodeFunctionData(
      "changeThreshold",
      proposal.data,
    );
    return BigInt(decoded[0]);
  } catch {
    return null;
  }
}

function App() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [configError, setConfigError] = useState("");
  const [view, setView] = useState<View>("connect");
  const [session, setSession] = useState<WalletSession | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [walletBusy, setWalletBusy] = useState(false);
  const [safeAddress, setSafeAddress] = useState(savedSafe);
  const [safeInput, setSafeInput] = useState(savedSafe);
  const [safeLabel, setSafeLabel] = useState("");
  const [snapshot, setSnapshot] = useState<SafeSnapshot | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [dashboardBusy, setDashboardBusy] = useState(false);
  const [customTokens, setCustomTokens] = useState<string[]>(savedTokens);
  const [customTokenInput, setCustomTokenInput] = useState("");
  const [created, setCreated] = useState<CreatedResult | null>(null);
  const [drafts, setDrafts] = useState<SafeRecord[]>([]);
  const [draftsLoaded, setDraftsLoaded] = useState(false);
  const [draftsBusy, setDraftsBusy] = useState(false);
  const [ownerAccessProof, setOwnerAccessProof] = useState<OwnerAccessProof | null>(null);
  const [manageAccessProof, setManageAccessProof] = useState<OwnerAccessProof | null>(null);
  const recoveryRequestId = useRef(0);
  const currentRecoveryAccount = useRef<string>();
  const walletEpoch = useRef(0);
  const walletBusyOperation = useRef<symbol | null>(null);
  const restoreRequest = useRef<{
    epoch: number;
    promise: Promise<WalletSession | null>;
  }>();
  currentRecoveryAccount.current = session?.account.toLowerCase();

  const resetWallet = useCallback(() => {
    walletEpoch.current += 1;
    walletBusyOperation.current = null;
    clearLastConnector();
    setSession(null);
    setWalletBusy(false);
    setNotice("");
    setView("connect");
  }, []);

  useEffect(() => {
    void loadConfig()
      .then(setConfig)
      .catch((caught: unknown) => setConfigError(errorText(caught)));
  }, []);

  useEffect(() => {
    if (!config) return;
    if (!restoreRequest.current) {
      restoreRequest.current = {
        epoch: walletEpoch.current,
        promise: restoreLastWallet(config),
      };
    }
    const request = restoreRequest.current;
    let active = true;
    void request.promise.then((restored) => {
      if (!active || !restored || request.epoch !== walletEpoch.current) return;
      setSession((current) => current ?? restored);
    });
    return () => {
      active = false;
    };
  }, [config]);

  useEffect(() => {
    if (!session) return;
    let active = true;

    const applyRefresh = (knownAccount?: string) => {
      const epoch = ++walletEpoch.current;
      void refreshSession(session, knownAccount)
        .then((refreshed) => {
          if (!active || epoch !== walletEpoch.current) return;
          if (!refreshed) {
            resetWallet();
            return;
          }
          setSession(refreshed);
        })
        .catch(() => {
          if (active && epoch === walletEpoch.current) resetWallet();
        });
    };
    const handleAccountsChanged = (...args: unknown[]) => {
      const accounts = args[0];
      if (!Array.isArray(accounts) || typeof accounts[0] !== "string") {
        resetWallet();
        return;
      }
      applyRefresh(accounts[0]);
    };
    const handleChainChanged = () => applyRefresh();
    const handleDisconnect = () => resetWallet();

    session.eip1193.on?.("accountsChanged", handleAccountsChanged);
    session.eip1193.on?.("chainChanged", handleChainChanged);
    session.eip1193.on?.("disconnect", handleDisconnect);
    return () => {
      active = false;
      session.eip1193.removeListener?.("accountsChanged", handleAccountsChanged);
      session.eip1193.removeListener?.("chainChanged", handleChainChanged);
      session.eip1193.removeListener?.("disconnect", handleDisconnect);
    };
  }, [resetWallet, session]);

  const tokenAddresses = useMemo(() => {
    if (!config) return customTokens;
    return [...new Set([config.wl1xTokenAddress, config.xusdTokenAddress, ...customTokens].filter(isAddress).map(getAddress))];
  }, [config, customTokens]);

  const refreshDrafts = useCallback(
    async (
      wallet: WalletSession | null,
      requestProof = false,
      requestId = recoveryRequestId.current,
      requestedAccount = wallet?.account.toLowerCase(),
    ) => {
      const requestIsCurrent = () =>
        requestId === recoveryRequestId.current &&
        requestedAccount === currentRecoveryAccount.current;
      if (!config || !wallet) {
        if (requestIsCurrent()) {
          setDrafts([]);
          setDraftsLoaded(false);
        }
        return;
      }
      let proof = ownerAccessProof;
      if (!ownerAccessProofIsFresh(proof, wallet.account)) {
        if (!requestProof) {
          if (requestIsCurrent()) {
            setDrafts([]);
            setDraftsLoaded(false);
          }
          return;
        }
        proof = await createOwnerAccessProof(config, wallet.signer, wallet.account);
        if (!requestIsCurrent()) return;
        setOwnerAccessProof(proof);
      }
      const creator = wallet.account.toLowerCase();
      const safes = await fetchSafes(config, proof);
      if (!requestIsCurrent()) return;
      setDrafts(
        safes.filter(
          (safe) => safe.status === "draft" && safe.creator?.toLowerCase() === creator,
        ),
      );
      setDraftsLoaded(true);
    },
    [config, ownerAccessProof],
  );

  useEffect(() => {
    recoveryRequestId.current += 1;
    setDrafts([]);
    setDraftsLoaded(false);
    setDraftsBusy(false);
    setOwnerAccessProof(null);
    setManageAccessProof(null);
  }, [session?.account]);

  async function loadRecoveryDrafts() {
    const requestId = ++recoveryRequestId.current;
    const requestedAccount = session?.account.toLowerCase();
    const requestIsCurrent = () =>
      requestId === recoveryRequestId.current &&
      requestedAccount === currentRecoveryAccount.current;
    setDraftsBusy(true);
    setError("");
    try {
      await refreshDrafts(session, true, requestId, requestedAccount);
    } catch (caught) {
      if (!requestIsCurrent()) return;
      setError(errorText(caught));
      setOwnerAccessProof(null);
    } finally {
      if (requestIsCurrent()) setDraftsBusy(false);
    }
  }

  const createManageProof = useCallback(
    async (wallet: WalletSession): Promise<OwnerAccessProof> => {
      if (!config) throw new Error("Application configuration is not loaded.");
      if (ownerAccessProofIsFresh(manageAccessProof, wallet.account)) return manageAccessProof;
      const proof = await createOwnerAccessProof(
        config,
        wallet.signer,
        wallet.account,
        OWNER_MANAGE_PURPOSE,
      );
      setManageAccessProof(proof);
      return proof;
    },
    [config, manageAccessProof],
  );

  const loadSafe = useCallback(
    async (address: string, shouldRegister = false, shouldApply: () => boolean = () => true) => {
      if (!config) return;
      if (!isAddress(address)) throw new Error("Enter a valid Safe address.");
      const checksummed = getAddress(address);
      setDashboardBusy(true);
      setError("");
      try {
        if (shouldRegister) {
          if (safeLabel.trim()) {
            if (!session) throw new Error("Connect a current Safe owner to save a private label.");
            if (session.chainId !== config.chainId) {
              throw new Error("Switch the connected wallet to L1X Mainnet to save a private label.");
            }
            const proof = await createManageProof(session);
            await registerSafe(config, checksummed, safeLabel, {}, proof);
          } else {
            await registerSafe(config, checksummed);
          }
        }
        const [nextSnapshot, nextProposals] = await Promise.all([
          getSafeSnapshot(config, checksummed, tokenAddresses),
          fetchPendingProposals(config, checksummed),
        ]);
        if (!shouldApply()) return;
        setSafeAddress(checksummed);
        setSafeInput(checksummed);
        setSnapshot(nextSnapshot);
        setProposals(nextProposals);
        localStorage.setItem("l1x-safe-address", checksummed);
        const url = new URL(window.location.href);
        url.searchParams.set("safe", checksummed);
        window.history.replaceState({}, "", url);
        setView("dashboard");
      } finally {
        setDashboardBusy(false);
      }
    },
    [config, createManageProof, safeLabel, session, tokenAddresses],
  );

  const refreshDashboard = useCallback(async () => {
    if (!safeAddress) return;
    await loadSafe(safeAddress);
  }, [loadSafe, safeAddress]);

  async function recoverSafeDraft(draft: SafeRecord) {
    if (!config || !session) throw new Error("Connect the draft creator wallet first.");
    if (session.chainId !== config.chainId) {
      throw new Error("Switch the connected wallet to L1X Mainnet first.");
    }
    const address = draft.predictedAddress ?? draft.address ?? (isAddress(safeInput) ? safeInput : null);
    if (!address || !isAddress(address) || !draft.draftId) {
      throw new Error("This recovery draft does not have a usable predicted address or draft ID.");
    }
    setDashboardBusy(true);
    setError("");
    try {
      const proof = await createManageProof(session);
      await registerSafe(
        config,
        getAddress(address),
        undefined,
        { draftId: draft.draftId },
        proof,
      );
      setDrafts((current) => current.filter((candidate) => candidate.draftId !== draft.draftId));
      setNotice("Recovery draft confirmed against the deployed Safe and opened.");
      await loadSafe(address);
    } finally {
      setDashboardBusy(false);
    }
  }

  async function connect(kind: WalletSession["kind"]) {
    if (!config) return;
    const epoch = ++walletEpoch.current;
    const busyOperation = Symbol("connect");
    walletBusyOperation.current = busyOperation;
    setWalletBusy(true);
    setError("");
    try {
      const next = kind === "injected" ? await connectInjected() : await connectWalletConnect(config);
      if (epoch !== walletEpoch.current) return;
      rememberConnector(next.kind);
      setSession(next);
      setNotice("Wallet connected. Network and signer address are shown below.");
      if (next.chainId === config.chainId && safeInput) {
        void loadSafe(safeInput, false, () => epoch === walletEpoch.current).catch(
          (caught: unknown) => {
            if (epoch === walletEpoch.current) setError(errorText(caught));
          },
        );
      }
    } catch (caught) {
      if (epoch === walletEpoch.current) setError(errorText(caught));
    } finally {
      if (walletBusyOperation.current === busyOperation) {
        walletBusyOperation.current = null;
        setWalletBusy(false);
      }
    }
  }

  async function changeNetwork() {
    if (!config || !session) return;
    const epoch = ++walletEpoch.current;
    const busyOperation = Symbol("change-network");
    walletBusyOperation.current = busyOperation;
    setWalletBusy(true);
    setError("");
    try {
      await switchOrAddL1X(session.eip1193, config);
      // A chain/disconnect event may have updated or cleared the session while the wallet
      // prompt was open. In that case its event handler owns the resulting UI state.
      if (epoch !== walletEpoch.current) return;
      const refreshed = await refreshSession(session);
      if (epoch !== walletEpoch.current) return;
      if (!refreshed) {
        resetWallet();
        return;
      }
      setSession(refreshed);
      setNotice("Wallet switched to L1X Mainnet.");
    } catch (caught) {
      if (epoch === walletEpoch.current) setError(errorText(caught));
    } finally {
      if (walletBusyOperation.current === busyOperation) {
        walletBusyOperation.current = null;
        setWalletBusy(false);
      }
    }
  }

  async function disconnect() {
    const connected = session;
    resetWallet();
    if (connected?.kind !== "walletconnect") return;
    try {
      await connected.eip1193.disconnect?.();
    } catch (caught) {
      console.warn("WalletConnect remote disconnect failed after local disconnect", caught);
    }
  }

  async function addToken() {
    if (!config) return;
    setError("");
    try {
      if (!isAddress(customTokenInput)) throw new Error("Enter a valid ERC-20 address.");
      const address = getAddress(customTokenInput);
      await tokenMetadata(readProvider(config), address);
      const next = [...new Set([...customTokens, address])];
      setCustomTokens(next);
      localStorage.setItem("l1x-safe-tokens", JSON.stringify(next));
      setCustomTokenInput("");
      setNotice("Token added. Refreshing balances…");
      if (safeAddress) {
        const nextSnapshot = await getSafeSnapshot(config, safeAddress, [
          config.wl1xTokenAddress,
          config.xusdTokenAddress,
          ...next,
        ]);
        setSnapshot(nextSnapshot);
      }
    } catch (caught) {
      setError(errorText(caught));
    }
  }

  if (configError) {
    return <main className="boot-message">Configuration failed: {configError}</main>;
  }
  if (!config) return <main className="boot-message">Loading L1X Safe…</main>;

  const wrongNetwork = Boolean(session && session.chainId !== config.chainId);

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" type="button" onClick={() => setView("connect")}>
          <span className="brand-mark"><img src="/l1x-logo.png" alt="L1X" /></span>
          <span>
            <strong>Safe</strong>
            <small>Multisig coordination</small>
          </span>
        </button>
        <nav aria-label="Primary navigation">
          <button className={view === "connect" ? "active" : ""} onClick={() => setView("connect")}>
            Connect
          </button>
          <button className={view === "create" ? "active" : ""} onClick={() => setView("create")}>
            Create Safe
          </button>
          <button
            className={view === "dashboard" ? "active" : ""}
            onClick={() => setView("dashboard")}
          >
            Dashboard
          </button>
        </nav>
        {session ? (
          <div className="wallet-pill">
            <span className={wrongNetwork ? "status-dot warning" : "status-dot"} />
            <AddressView address={session.account} explorerUrl={config.explorerUrl} />
            <button
              type="button"
              className="wallet-disconnect"
              onClick={() => void disconnect()}
              disabled={walletBusy}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <span className="connection-state">Not connected</span>
        )}
      </header>

      {wrongNetwork ? (
        <div className="network-banner">
          <span>Your wallet is on chain {session?.chainId}. L1X Mainnet (1066) is required.</span>
          <button onClick={() => void changeNetwork()} disabled={walletBusy}>
            Add / switch to L1X
          </button>
        </div>
      ) : null}
      {error ? (
        <div className="message error-message" role="alert">
          <span>{error}</span>
          <button type="button" onClick={() => setError("")}>
            Dismiss
          </button>
        </div>
      ) : null}
      {notice ? (
        <div className="message notice-message" role="status">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice("")}>
            Dismiss
          </button>
        </div>
      ) : null}

      <main className="main-content">
        {view === "connect" ? (
          <ConnectScreen
            config={config}
            session={session}
            busy={walletBusy}
            onConnect={connect}
            onSwitch={changeNetwork}
            onDisconnect={disconnect}
          />
        ) : null}

        {view === "create" ? (
          <CreateSafeScreen
            config={config}
            session={session}
            wrongNetwork={wrongNetwork}
            created={created}
            setCreated={setCreated}
            onOpenDashboard={() => {
              setView("dashboard");
              void loadSafe(created?.address ?? safeAddress).catch((caught: unknown) =>
                setError(errorText(caught)),
              );
            }}
            onCreated={async (
              result,
              label,
              registration,
              predictedAddress,
              predictionMismatch,
              manageProof,
            ) => {
              setCreated(result);
              setSafeAddress(result.address);
              setSafeInput(result.address);
              localStorage.setItem("l1x-safe-address", result.address);
              const url = new URL(window.location.href);
              url.searchParams.set("safe", result.address);
              window.history.replaceState({}, "", url);
              try {
                const proof =
                  session && ownerAccessProofIsFresh(manageProof ?? null, session.account)
                    ? manageProof
                    : session
                      ? await createManageProof(session)
                      : undefined;
                await registerSafe(config, result.address, label, registration, proof);
                if (predictionMismatch) {
                  setNotice("Safe created and registered at the address emitted by the factory.");
                  setError(
                    `Warning: the factory created ${result.address}, but the predicted address was ${predictedAddress}. ` +
                      "The mismatch was not ignored; use the factory-emitted address shown on this screen.",
                  );
                } else {
                  setNotice("Safe created and registered with the coordination service.");
                }
              } catch (caught) {
                setError(
                  `The Safe was created on-chain, but registration failed: ${errorText(caught)}` +
                    (predictionMismatch
                      ? ` The predicted address ${predictedAddress} also differed from the factory-emitted address ${result.address}.`
                      : ""),
                );
              } finally {
                try {
                  await refreshDrafts(session);
                } catch (caught) {
                  console.warn("Could not refresh Safe recovery drafts", caught);
                }
              }
            }}
            onDraftChanged={() => {
              void refreshDrafts(session).catch((caught: unknown) => {
                console.warn("Could not refresh Safe recovery drafts", caught);
              });
            }}
            createManageProof={createManageProof}
            onError={(message) => setError(message)}
            onNotice={(message) => setNotice(message)}
          />
        ) : null}

        {view === "dashboard" ? (
          <DashboardScreen
            config={config}
            session={session}
            wrongNetwork={wrongNetwork}
            safeInput={safeInput}
            setSafeInput={setSafeInput}
            safeLabel={safeLabel}
            setSafeLabel={setSafeLabel}
            snapshot={snapshot}
            proposals={proposals}
            drafts={drafts}
            draftsLoaded={draftsLoaded}
            draftsBusy={draftsBusy}
            busy={dashboardBusy}
            onOpen={() =>
              void loadSafe(safeInput, true).catch((caught: unknown) => setError(errorText(caught)))
            }
            onRefresh={() =>
              void refreshDashboard().catch((caught: unknown) => setError(errorText(caught)))
            }
            onLoadDrafts={() => void loadRecoveryDrafts()}
            onRecoverDraft={(draft) =>
              void recoverSafeDraft(draft).catch((caught: unknown) => setError(errorText(caught)))
            }
            customTokenInput={customTokenInput}
            setCustomTokenInput={setCustomTokenInput}
            onAddToken={() => void addToken()}
            onError={(message) => setError(message)}
            onNotice={(message) => setNotice(message)}
          />
        ) : null}
      </main>

      <footer>
        <span>Chain-enforced custody. This interface never handles private keys.</span>
        <a href={config.explorerUrl} target="_blank" rel="noreferrer">
          L1X Explorer ↗
        </a>
      </footer>
    </div>
  );
}

function ConnectScreen({
  config,
  session,
  busy,
  onConnect,
  onSwitch,
  onDisconnect,
}: {
  config: AppConfig;
  session: WalletSession | null;
  busy: boolean;
  onConnect: (kind: WalletSession["kind"]) => Promise<void>;
  onSwitch: () => Promise<void>;
  onDisconnect: () => Promise<void>;
}) {
  const missing = missingCreationContracts(config);
  return (
    <section className="hero-grid">
      <div className="hero-copy">
        <span className="eyebrow">SAFE v1.4.1 · L1X MAINNET</span>
        <h1>Shared custody, without shared secrets.</h1>
        <p>
          Create, review, sign, and execute multisig transfers on L1X. Owners sign in their own
          wallets; private keys never enter this application.
        </p>
        {!session ? (
          <div className="button-row">
            <button
              className="primary-button"
              onClick={() => void onConnect("injected")}
              disabled={busy || !hasInjectedWallet()}
            >
              {busy ? <SpinnerLabel>Connecting</SpinnerLabel> : "Connect MetaMask"}
            </button>
            {config.walletConnectProjectId ? (
              <button className="secondary-button" onClick={() => void onConnect("walletconnect")} disabled={busy}>
                WalletConnect
              </button>
            ) : null}
          </div>
        ) : (
          <div className="card wallet-card">
            <div>
              <span className="detail-label">Connected owner</span>
              <AddressView address={session.account} explorerUrl={config.explorerUrl} full />
            </div>
            <div>
              <span className="detail-label">Network</span>
              <strong>{session.chainId === config.chainId ? config.chainName : `Chain ${session.chainId}`}</strong>
            </div>
            <div className="button-row">
              {session.chainId !== config.chainId ? (
                <button className="primary-button" onClick={() => void onSwitch()} disabled={busy}>
                  Add / switch L1X
                </button>
              ) : null}
              <button className="secondary-button" onClick={() => void onDisconnect()}>
                Disconnect
              </button>
            </div>
          </div>
        )}
        {!hasInjectedWallet() && !config.walletConnectProjectId ? (
          <p className="inline-warning">No injected wallet detected and WalletConnect is not configured.</p>
        ) : null}
      </div>

      <aside className="card config-card">
        <div className="card-heading">
          <div>
            <span className="eyebrow">DEPLOYMENT</span>
            <h2>Contract configuration</h2>
          </div>
          {missing.length ? (
            <span className="badge badge-warning">Needs config</span>
          ) : null}
        </div>
        {missing.length ? (
          <p className="inline-warning">Missing: {missing.join(", ")}. Dashboard reads still work; creation is disabled.</p>
        ) : null}
        <dl className="detail-list">
          <div>
            <dt>Safe singleton</dt>
            <dd><AddressView address={config.contracts.safeSingleton} explorerUrl={config.explorerUrl} /></dd>
          </div>
          <div>
            <dt>Proxy factory</dt>
            <dd><AddressView address={config.contracts.safeProxyFactory} explorerUrl={config.explorerUrl} /></dd>
          </div>
          <div>
            <dt>Fallback handler</dt>
            <dd><AddressView address={config.contracts.compatibilityFallbackHandler} explorerUrl={config.explorerUrl} /></dd>
          </div>
          <div>
            <dt>WL1X token</dt>
            <dd><AddressView address={config.wl1xTokenAddress} explorerUrl={config.explorerUrl} /></dd>
          </div>
        </dl>
      </aside>
    </section>
  );
}

function CreateSafeScreen({
  config,
  session,
  wrongNetwork,
  created,
  setCreated,
  onOpenDashboard,
  onCreated,
  onDraftChanged,
  createManageProof,
  onError,
  onNotice,
}: {
  config: AppConfig;
  session: WalletSession | null;
  wrongNetwork: boolean;
  created: CreatedResult | null;
  setCreated: (result: CreatedResult | null) => void;
  onOpenDashboard: () => void;
  onCreated: (
    result: CreatedResult,
    label: string,
    registration: SafeRegistrationMetadata,
    predictedAddress: string | null,
    predictionMismatch: boolean,
    manageProof?: OwnerAccessProof,
  ) => Promise<void>;
  onDraftChanged: () => void;
  createManageProof: (wallet: WalletSession) => Promise<OwnerAccessProof>;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}) {
  const [owners, setOwners] = useState([session?.account ?? "", ""]);
  const [threshold, setThreshold] = useState(2);
  const [salt, setSalt] = useState(() => `l1x-safe-${Date.now()}`);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryDraftId, setRecoveryDraftId] = useState<string>();
  const missing = missingCreationContracts(config);

  useEffect(() => {
    if (session?.account && !owners[0]) setOwners((current) => [session.account, ...current.slice(1)]);
  }, [owners, session]);

  function updateOwner(index: number, value: string) {
    setRecoveryDraftId(undefined);
    setOwners((current) => current.map((owner, ownerIndex) => (ownerIndex === index ? value : owner)));
  }

  function removeOwner(index: number) {
    setRecoveryDraftId(undefined);
    setOwners((current) => {
      if (current.length <= 1) return current;
      const next = current.filter((_, ownerIndex) => ownerIndex !== index);
      setThreshold((value) => Math.min(value, next.length));
      return next;
    });
  }

  async function submit() {
    if (!session) return onError("Connect a wallet first.");
    if (wrongNetwork) return onError("Switch the connected wallet to L1X Mainnet first.");
    setBusy(true);
    onError("");
    try {
      const checkedOwners = owners.map((owner) => getAddress(owner));
      encodeSafeSetup(
        checkedOwners,
        threshold,
        config.contracts.compatibilityFallbackHandler,
      );
      const normalizedSalt = parseSaltNonce(salt).toString();

      let predictedAddress: string | null = null;
      let predictionFailed = false;
      try {
        predictedAddress = await predictSafeAddress(
          config,
          checkedOwners,
          threshold,
          normalizedSalt,
        );
      } catch (caught) {
        predictionFailed = true;
        console.warn("Could not predict the Safe address; saving an address-less draft", caught);
      }

      let promotionDraftId = recoveryDraftId;
      let manageProof: OwnerAccessProof | undefined;
      try {
        manageProof = await createManageProof(session);
        const draft = await createSafeDraft(config, {
          predictedAddress,
          ...(recoveryDraftId ? { draftId: recoveryDraftId } : {}),
          chainId: config.chainId,
          owners: checkedOwners,
          threshold,
          ...(label.trim() ? { label: label.trim() } : {}),
          salt: normalizedSalt,
          creator: session.account,
        }, manageProof);
        promotionDraftId = draft.draftId ?? recoveryDraftId;
        setRecoveryDraftId(promotionDraftId);
        onDraftChanged();
        if (predictionFailed) {
          onNotice(
            "The recovery draft was saved without a predicted address. Safe creation will continue.",
          );
        }
      } catch (caught) {
        console.warn("Could not save the pre-transaction Safe recovery draft", caught);
        onNotice(
          "The recovery draft could not be saved. This is non-fatal; Safe creation will continue.",
        );
      }

      const result = await createSafeProxy(
        config,
        session.signer,
        checkedOwners,
        threshold,
        normalizedSalt,
      );
      const predictionMismatch = Boolean(
        predictedAddress && getAddress(result.address) !== getAddress(predictedAddress),
      );
      await onCreated(
        result,
        label,
        {
          creationTxHash: result.transactionHash,
          salt: normalizedSalt,
          creator: session.account,
          ...(promotionDraftId ? { draftId: promotionDraftId } : {}),
        },
        predictedAddress,
        predictionMismatch,
        manageProof,
      );
    } catch (caught) {
      onError(errorText(caught));
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <section className="narrow-section">
        <div className="success-mark">✓</div>
        <span className="eyebrow">ON-CHAIN VERIFIED</span>
        <h1>Safe created</h1>
        <p className="lead">The proxy receipt succeeded and owners plus threshold were read back from L1X.</p>
        <div className="card success-card">
          <div>
            <span className="detail-label">Safe address</span>
            <AddressView address={created.address} explorerUrl={config.explorerUrl} full />
          </div>
          <div>
            <span className="detail-label">Owners</span>
            <div className="stacked-addresses">
              {created.owners.map((owner) => <AddressView key={owner} address={owner} explorerUrl={config.explorerUrl} full />)}
            </div>
          </div>
          <div>
            <span className="detail-label">Threshold</span>
            <strong>{created.threshold} of {created.owners.length}</strong>
          </div>
          <div>
            <span className="detail-label">Creation transaction</span>
            <TxView hash={created.transactionHash} explorerUrl={config.explorerUrl} />
          </div>
        </div>
        <div className="button-row">
          <button className="primary-button" onClick={onOpenDashboard}>Open dashboard</button>
          <button
            className="secondary-button"
            onClick={() => {
              setRecoveryDraftId(undefined);
              setSalt(`l1x-safe-${Date.now()}`);
              setCreated(null);
            }}
          >
            Create another Safe
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="narrow-section">
      <span className="eyebrow">NEW MULTISIG</span>
      <h1>Create a Safe</h1>
      <p className="lead">The connected wallet deploys a v1.4.1 proxy through the configured L1X factory.</p>
      <div className="card form-card">
        <Field label="Safe label" hint="Stored by the coordination service; not written on-chain.">
          <input value={label} onChange={(event) => setLabel(event.target.value)} maxLength={80} placeholder="e.g. Treasury 2-of-2" />
        </Field>
        <div className="field">
          <span className="field-label">Owners</span>
          <div className="owners-list">
            {owners.map((owner, index) => (
              <div className="owner-row" key={index}>
                <span className="owner-number">{index + 1}</span>
                <input
                  aria-label={`Owner ${index + 1} address`}
                  value={owner}
                  onChange={(event) => updateOwner(index, event.target.value)}
                  placeholder="0x…"
                  spellCheck={false}
                />
                {isAddress(owner) ? <AddressView address={owner} explorerUrl={config.explorerUrl} /> : null}
                <button className="remove-button" type="button" onClick={() => removeOwner(index)} disabled={owners.length <= 1}>
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            className="text-button"
            type="button"
            onClick={() => {
              setRecoveryDraftId(undefined);
              setOwners((current) => [...current, ""]);
            }}
          >
            + Add owner
          </button>
        </div>
        <div className="two-column">
          <Field label="Threshold" hint={`${threshold} owner${threshold === 1 ? "" : "s"} must sign.`}>
            <select
              value={threshold}
              onChange={(event) => {
                setRecoveryDraftId(undefined);
                setThreshold(Number(event.target.value));
              }}
            >
              {owners.map((_, index) => <option value={index + 1} key={index + 1}>{index + 1} of {owners.length}</option>)}
            </select>
          </Field>
          <Field label="Salt" hint="Text is deterministically hashed to uint256; numeric salts are used directly.">
            <input
              value={salt}
              onChange={(event) => {
                setRecoveryDraftId(undefined);
                setSalt(event.target.value);
              }}
              spellCheck={false}
            />
          </Field>
        </div>
        {missing.length ? <p className="inline-warning">Creation disabled. Missing {missing.join(", ")}.</p> : null}
        <button
          className="primary-button wide-button"
          onClick={() => void submit()}
          disabled={busy || !session || wrongNetwork || missing.length > 0}
        >
          {busy ? (
            <SpinnerLabel>Waiting for on-chain verification</SpinnerLabel>
          ) : !session ? (
            "Connect wallet to create your Safe"
          ) : wrongNetwork ? (
            "Switch to L1X to create your Safe"
          ) : (
            "Create Safe on L1X"
          )}
        </button>
      </div>
    </section>
  );
}

function DashboardScreen({
  config,
  session,
  wrongNetwork,
  safeInput,
  setSafeInput,
  safeLabel,
  setSafeLabel,
  snapshot,
  proposals,
  drafts,
  draftsLoaded,
  draftsBusy,
  busy,
  onOpen,
  onRefresh,
  onLoadDrafts,
  onRecoverDraft,
  customTokenInput,
  setCustomTokenInput,
  onAddToken,
  onError,
  onNotice,
}: {
  config: AppConfig;
  session: WalletSession | null;
  wrongNetwork: boolean;
  safeInput: string;
  setSafeInput: (value: string) => void;
  safeLabel: string;
  setSafeLabel: (value: string) => void;
  snapshot: SafeSnapshot | null;
  proposals: Proposal[];
  drafts: SafeRecord[];
  draftsLoaded: boolean;
  draftsBusy: boolean;
  busy: boolean;
  onOpen: () => void;
  onRefresh: () => void;
  onLoadDrafts: () => void;
  onRecoverDraft: (draft: SafeRecord) => void;
  customTokenInput: string;
  setCustomTokenInput: (value: string) => void;
  onAddToken: () => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}) {
  return (
    <section>
      <div className="dashboard-header">
        <div>
          <span className="eyebrow">SAFE DASHBOARD</span>
          <h1>{snapshot ? "Treasury overview" : "Open a Safe"}</h1>
        </div>
        {snapshot ? (
          <button className="secondary-button" onClick={onRefresh} disabled={busy}>
            {busy ? <SpinnerLabel>Refreshing</SpinnerLabel> : "Refresh"}
          </button>
        ) : null}
      </div>

      <div className="card open-safe-card">
        <Field label="Safe address">
          <input value={safeInput} onChange={(event) => setSafeInput(event.target.value)} placeholder="0x…" spellCheck={false} />
        </Field>
        <Field label="Private label (optional)" hint="Saving a label requires a short-lived signature from a current owner.">
          <input value={safeLabel} onChange={(event) => setSafeLabel(event.target.value)} maxLength={80} placeholder="Partner treasury" />
        </Field>
        <button className="primary-button" onClick={onOpen} disabled={busy || !isAddress(safeInput)}>
          {busy ? <SpinnerLabel>Reading L1X</SpinnerLabel> : safeLabel.trim() ? "Save label / open" : "Open Safe"}
        </button>
      </div>

      {session ? (
        <div className="card draft-recovery-card">
          <div>
            <span className="eyebrow">RECOVERY</span>
            <h2>Interrupted Safe creations</h2>
            <p className="subtle">
              Sign a short-lived access message to load private labels and recovery drafts for
              the connected wallet. This does not submit a transaction.
            </p>
          </div>
          {!draftsLoaded ? (
            <button
              className="secondary-button"
              type="button"
              onClick={onLoadDrafts}
              disabled={draftsBusy || wrongNetwork}
            >
              {draftsBusy ? <SpinnerLabel>Checking owner access</SpinnerLabel> : "Load recovery drafts"}
            </button>
          ) : drafts.length === 0 ? (
            <p className="subtle">No interrupted Safe creations were found for this wallet.</p>
          ) : (
            <div className="draft-recovery-list">
            {drafts.map((draft) => {
              const predictedAddress = draft.predictedAddress ?? draft.address;
              const hasPredictedAddress = Boolean(
                predictedAddress && isAddress(predictedAddress),
              );
              const hasRecoveryAddress = hasPredictedAddress || isAddress(safeInput);
              return (
                <div
                  className="draft-recovery-row"
                  key={draft.draftId || predictedAddress || draft.createdAt}
                >
                  <div>
                    <span className="detail-label">{draft.label || "Unlabelled Safe"}</span>
                    {hasPredictedAddress && predictedAddress ? (
                      <AddressView
                        address={predictedAddress}
                        explorerUrl={config.explorerUrl}
                        full
                      />
                    ) : (
                      <span className="mono muted">Predicted address unavailable</span>
                    )}
                  </div>
                  {hasRecoveryAddress ? (
                    <button
                      className="text-button"
                      type="button"
                      onClick={() => onRecoverDraft(draft)}
                      disabled={busy || wrongNetwork}
                    >
                      {hasPredictedAddress ? "Recover & open" : "Recover entered address"}
                    </button>
                  ) : null}
                </div>
              );
            })}
            </div>
          )}
        </div>
      ) : null}

      {snapshot ? (
        <>
          <div className="metrics-grid">
            <article className="metric-card accent-card">
              <span>Native balance</span>
              <strong>{numberLabel(snapshot.nativeBalance)} L1X</strong>
              <AddressView address={snapshot.address} explorerUrl={config.explorerUrl} />
            </article>
            {snapshot.tokens.map((token) => (
              <article className="metric-card" key={token.address}>
                <span>{token.address === config.wl1xTokenAddress ? "WL1X balance" : `${token.symbol} balance`}</span>
                <strong>{numberLabel(token.balance, token.decimals)} {token.symbol}</strong>
                <AddressView address={token.address} explorerUrl={config.explorerUrl} />
              </article>
            ))}
            <article className="metric-card">
              <span>Approval policy</span>
              <strong>{snapshot.threshold} of {snapshot.owners.length}</strong>
              <small>Safe v{snapshot.version} · nonce {snapshot.nonce.toString()}</small>
            </article>
          </div>

          <div className="dashboard-columns">
            <div className="dashboard-main">
              <ProposalComposer
                config={config}
                session={session}
                wrongNetwork={wrongNetwork}
                snapshot={snapshot}
                onCreated={onRefresh}
                onError={onError}
                onNotice={onNotice}
              />
              <ProposalList
                config={config}
                session={session}
                wrongNetwork={wrongNetwork}
                snapshot={snapshot}
                proposals={proposals}
                onRefresh={onRefresh}
                onError={onError}
                onNotice={onNotice}
              />
            </div>
            <aside className="dashboard-side">
              <div className="card">
                <div className="card-heading"><h2>Owners</h2><span className="badge">{snapshot.owners.length}</span></div>
                <div className="owner-cards">
                  {snapshot.owners.map((owner, index) => (
                    <div className="owner-card" key={owner}>
                      <span className="owner-number">{index + 1}</span>
                      <AddressView address={owner} explorerUrl={config.explorerUrl} />
                      {session?.account.toLowerCase() === owner.toLowerCase() ? <span className="you-badge">You</span> : null}
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <h2>Add ERC-20</h2>
                <p className="subtle">Saved in this browser and read directly from L1X.</p>
                <input value={customTokenInput} onChange={(event) => setCustomTokenInput(event.target.value)} placeholder="Token address 0x…" spellCheck={false} />
                {isAddress(customTokenInput) ? <AddressView address={customTokenInput} explorerUrl={config.explorerUrl} /> : null}
                <button className="secondary-button wide-button" onClick={onAddToken} disabled={!isAddress(customTokenInput)}>Add token</button>
              </div>
            </aside>
          </div>
        </>
      ) : (
        <EmptyState>Register an existing Safe to verify it on-chain and load balances and proposals.</EmptyState>
      )}
    </section>
  );
}

function ProposalComposer({
  config,
  session,
  wrongNetwork,
  snapshot,
  onCreated,
  onError,
  onNotice,
}: {
  config: AppConfig;
  session: WalletSession | null;
  wrongNetwork: boolean;
  snapshot: SafeSnapshot;
  onCreated: () => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}) {
  const defaultAsset = snapshot.tokens.find((token) => token.address === config.wl1xTokenAddress)?.address ?? "native";
  const [proposalType, setProposalType] = useState<ProposalType>("transfer");
  const [asset, setAsset] = useState(defaultAsset);
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [proposedThreshold, setProposedThreshold] = useState(snapshot.threshold);
  const [busy, setBusy] = useState(false);
  const [createdHash, setCreatedHash] = useState("");
  const isOwner = Boolean(
    session && snapshot.owners.some((owner) => owner.toLowerCase() === session.account.toLowerCase()),
  );
  const proposedThresholdIsValid =
    Number.isInteger(proposedThreshold) &&
    proposedThreshold >= 1 &&
    proposedThreshold <= snapshot.owners.length &&
    proposedThreshold !== snapshot.threshold;

  useEffect(() => {
    setProposedThreshold(snapshot.threshold);
  }, [snapshot.address, snapshot.owners.length, snapshot.threshold]);

  async function submit() {
    if (!session) {
      return onError(
        proposalType === "transfer"
          ? "Connect an owner wallet to propose a transfer."
          : "Connect an owner wallet to propose a threshold change.",
      );
    }
    if (wrongNetwork) return onError("Switch to L1X Mainnet first.");
    if (!isOwner) {
      return onError("Only a current Safe owner can create a proposal.");
    }
    setBusy(true);
    onError("");
    try {
      let to: string;
      let value: bigint;
      let data: string;
      if (proposalType === "transfer") {
        to = asset === "native" ? getAddress(recipient) : getAddress(asset);
        const token = snapshot.tokens.find((item) => item.address === asset);
        if (asset !== "native" && !token) throw new Error("Selected token metadata is unavailable.");
        const parsedAmount = asset === "native" ? parseEther(amount) : parseUnits(amount, token?.decimals ?? 18);
        if (parsedAmount <= 0n) throw new Error("Amount must be greater than zero.");
        if (getAddress(recipient) === ZeroAddress) throw new Error("Recipient cannot be the zero address.");
        const availableBalance = asset === "native" ? snapshot.nativeBalance : token?.balance ?? 0n;
        if (parsedAmount > availableBalance) throw new Error("Amount exceeds the Safe's current balance.");
        value = asset === "native" ? parsedAmount : 0n;
        data = asset === "native" ? "0x" : encodeErc20Transfer(recipient, parsedAmount);
      } else {
        if (!Number.isInteger(proposedThreshold)) {
          throw new Error("Threshold must be a whole number.");
        }
        if (proposedThreshold < 1 || proposedThreshold > snapshot.owners.length) {
          throw new Error(`Threshold must be between 1 and ${snapshot.owners.length}.`);
        }
        if (proposedThreshold === snapshot.threshold) {
          throw new Error("Choose a threshold different from the current threshold.");
        }
        to = snapshot.address;
        value = 0n;
        data = CHANGE_THRESHOLD_INTERFACE.encodeFunctionData("changeThreshold", [
          proposedThreshold,
        ]);
      }
      const transaction = {
        to,
        value,
        data,
        operation: 0,
        safeTxGas: 0n,
        baseGas: 0n,
        gasPrice: 0n,
        gasToken: ZeroAddress,
        refundReceiver: ZeroAddress,
        nonce: snapshot.nonce,
      };
      const safeTxHash = await assertSafeTransactionHash(config, snapshot.address, transaction);
      const walletSignature = await session.signer.signTypedData(
        safeDomain(config, snapshot.address),
        SAFE_TX_TYPES,
        transaction,
      );
      if (recoverSafeSigner(safeTxHash, walletSignature).toLowerCase() !== session.account.toLowerCase()) {
        throw new Error("Wallet signature recovery did not match the connected account.");
      }
      const input: ProposalInput = {
        to: transaction.to,
        value: transaction.value.toString(),
        data: transaction.data,
        operation: transaction.operation,
        safeTxGas: transaction.safeTxGas.toString(),
        baseGas: transaction.baseGas.toString(),
        gasPrice: transaction.gasPrice.toString(),
        gasToken: transaction.gasToken,
        refundReceiver: transaction.refundReceiver,
        nonce: transaction.nonce.toString(),
        safeTxHash,
        signature: normaliseSafeSignature(safeTxHash, walletSignature),
      };
      await createProposal(config, snapshot.address, input);
      setCreatedHash(safeTxHash);
      setRecipient("");
      setAmount("");
      setProposedThreshold(snapshot.threshold);
      onNotice("Proposal created and your owner signature was stored as its first approval.");
      onCreated();
    } catch (caught) {
      onError(errorText(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card proposal-composer">
      <div className="card-heading">
        <div>
          <span className="eyebrow">NEW TRANSACTION</span>
          <h2>{proposalType === "transfer" ? "Propose transfer" : "Change threshold"}</h2>
        </div>
        <div className="proposal-type-switch" role="group" aria-label="Proposal type">
          <button
            type="button"
            className={proposalType === "transfer" ? "active" : ""}
            aria-pressed={proposalType === "transfer"}
            onClick={() => setProposalType("transfer")}
            disabled={busy}
          >
            Transfer
          </button>
          <button
            type="button"
            className={proposalType === "threshold" ? "active" : ""}
            aria-pressed={proposalType === "threshold"}
            onClick={() => {
              setProposedThreshold(snapshot.threshold);
              setProposalType("threshold");
            }}
            disabled={busy}
          >
            Change threshold
          </button>
        </div>
      </div>
      {proposalType === "transfer" ? (
        <>
          <div className="three-column">
            <Field label="Asset">
              <select value={asset} onChange={(event) => setAsset(event.target.value)}>
                <option value="native">L1X (native)</option>
                {snapshot.tokens.map((token) => (
                  <option value={token.address} key={token.address}>
                    {token.address === config.wl1xTokenAddress ? "WL1X" : token.symbol}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Recipient">
              <input value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="0x…" spellCheck={false} />
            </Field>
            <Field label="Amount">
              <input value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0.0" inputMode="decimal" />
            </Field>
          </div>
          {isAddress(recipient) ? <div className="input-address"><AddressView address={recipient} explorerUrl={config.explorerUrl} /></div> : null}
        </>
      ) : (
        <div className="threshold-form">
          <Field
            label="New threshold"
            hint={`${proposedThreshold} of ${snapshot.owners.length} owners must approve every transaction.`}
          >
            <select
              value={proposedThreshold}
              onChange={(event) => setProposedThreshold(Number(event.target.value))}
            >
              {snapshot.owners.map((_, index) => {
                const value = index + 1;
                return (
                  <option
                    value={value}
                    key={value}
                    disabled={value === snapshot.threshold}
                  >
                    {value} of {snapshot.owners.length}
                    {value === snapshot.threshold ? " (current)" : ""}
                  </option>
                );
              })}
            </select>
          </Field>
        </div>
      )}
      <p className="subtle">Proposing signs the Safe transaction hash in your wallet and counts as the first owner approval.</p>
      <button
        className="primary-button"
        onClick={() => void submit()}
        disabled={
          busy ||
          !session ||
          wrongNetwork ||
          !isOwner ||
          (proposalType === "transfer"
            ? !isAddress(recipient) || !amount
            : !proposedThresholdIsValid)
        }
      >
        {busy ? (
          <SpinnerLabel>Confirm signature in wallet</SpinnerLabel>
        ) : !session ? (
          "Connect an owner wallet to propose"
        ) : wrongNetwork ? (
          "Switch to L1X to propose"
        ) : !isOwner ? (
          "Connected wallet is not a Safe owner"
        ) : (
          "Sign & create proposal"
        )}
      </button>
      {createdHash ? (
        <div className="hash-callout">
          <span>Safe transaction hash — compare this exact value out of band</span>
          <HashView value={createdHash} />
        </div>
      ) : null}
    </div>
  );
}

function ProposalList({
  config,
  session,
  wrongNetwork,
  snapshot,
  proposals,
  onRefresh,
  onError,
  onNotice,
}: {
  config: AppConfig;
  session: WalletSession | null;
  wrongNetwork: boolean;
  snapshot: SafeSnapshot;
  proposals: Proposal[];
  onRefresh: () => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}) {
  const [busyId, setBusyId] = useState<string | number | null>(null);
  const [executionHash, setExecutionHash] = useState("");

  async function sign(proposal: Proposal) {
    if (!session) return onError("Connect an owner wallet first.");
    if (wrongNetwork) return onError("Switch to L1X Mainnet first.");
    const isOwner = snapshot.owners.some((owner) => owner.toLowerCase() === session.account.toLowerCase());
    if (!isOwner) return onError("The connected address is not a Safe owner.");
    setBusyId(proposal.id);
    onError("");
    try {
      const transaction = proposalTransaction(proposal);
      await assertSafeTransactionHash(config, snapshot.address, transaction, proposal.safeTxHash);
      const signature = await session.signer.signTypedData(
        safeDomain(config, snapshot.address),
        SAFE_TX_TYPES,
        transaction,
      );
      if (recoverSafeSigner(proposal.safeTxHash, signature) !== session.account) {
        throw new Error("Wallet signature recovery did not match the connected account.");
      }
      await submitSignature(
        config,
        proposal.id,
        normaliseSafeSignature(proposal.safeTxHash, signature),
        session.account,
      );
      onNotice("Signature verified and stored by the coordination service.");
      onRefresh();
    } catch (caught) {
      onError(errorText(caught));
    } finally {
      setBusyId(null);
    }
  }

  async function execute(proposal: Proposal) {
    if (!session) return onError("Connect a wallet to submit the execution transaction.");
    if (wrongNetwork) return onError("Switch to L1X Mainnet first.");
    setBusyId(proposal.id);
    onError("");
    try {
      if (BigInt(proposal.nonce) !== snapshot.nonce) {
        throw new Error(`Proposal nonce ${proposal.nonce} is stale; the Safe is now at nonce ${snapshot.nonce}.`);
      }
      const valid = uniqueValidSignatures(proposal.safeTxHash, proposal.signatures, snapshot.owners);
      if (valid.length < snapshot.threshold) throw new Error("The Safe threshold has not been met.");
      const txHash = await executeProposal(config, session.signer, snapshot.address, proposal, snapshot.owners);
      setExecutionHash(txHash);
      await reportExecution(config, proposal.id, txHash);
      onNotice("Safe transaction executed and confirmed by the coordination service.");
      onRefresh();
    } catch (caught) {
      onError(errorText(caught));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="proposal-section">
      <div className="section-heading"><div><span className="eyebrow">COORDINATION</span><h2>Pending proposals</h2></div><span className="badge">{proposals.length}</span></div>
      {executionHash ? (
        <div className="message notice-message"><span>Last execution</span><TxView hash={executionHash} explorerUrl={config.explorerUrl} /></div>
      ) : null}
      {proposals.length === 0 ? <EmptyState>No pending proposals.</EmptyState> : null}
      <div className="proposal-list">
        {proposals.map((proposal) => {
          const valid = uniqueValidSignatures(proposal.safeTxHash, proposal.signatures, snapshot.owners);
          const signed = new Set(valid.map((item) => item.recoveredSigner.toLowerCase()));
          const missing = snapshot.owners.filter((owner) => !signed.has(owner.toLowerCase()));
          const accountSigned = session ? signed.has(session.account.toLowerCase()) : false;
          const ready = valid.length >= snapshot.threshold;
          const thresholdChange = decodeThresholdChange(proposal, snapshot.address);
          const token = thresholdChange === null
            ? snapshot.tokens.find((item) => item.address.toLowerCase() === proposal.to.toLowerCase())
            : undefined;
          let proposalKind = proposal.operation === 1 ? "DelegateCall" : "Native transfer";
          let recipient = proposal.to;
          let recipientLabel = "Recipient";
          let amount = `${formatEther(BigInt(proposal.value))} L1X`;
          if (thresholdChange !== null) {
            proposalKind = "Safe settings";
            amount = `Change threshold to ${thresholdChange.toString()}`;
            recipientLabel = "Safe";
          } else if (token && proposal.data !== "0x") {
            try {
              const decoded = new Interface(ERC20_ABI).decodeFunctionData("transfer", proposal.data);
              recipient = getAddress(String(decoded[0]));
              amount = `${numberLabel(BigInt(decoded[1]), token.decimals)} ${token.symbol}`;
              proposalKind = "ERC-20 transfer";
            } catch {
              amount = `ERC-20 call to ${token.symbol}`;
              proposalKind = "Contract call";
            }
          } else if (proposal.data !== "0x") {
            amount = "Contract interaction";
            proposalKind = proposal.operation === 1 ? "DelegateCall" : "Contract call";
          }
          return (
            <article className="card proposal-card" key={proposal.id}>
              <div className="proposal-topline">
                <div><span className="detail-label">{proposalKind}</span><strong className="proposal-amount">{amount}</strong></div>
                <span className={`badge ${ready ? "badge-success" : ""}`}>{valid.length} / {snapshot.threshold} signed</span>
              </div>
              <dl className="proposal-details">
                <div><dt>{recipientLabel}</dt><dd><AddressView address={recipient} explorerUrl={config.explorerUrl} /></dd></div>
                {token ? <div><dt>Token contract</dt><dd><AddressView address={token.address} explorerUrl={config.explorerUrl} /></dd></div> : null}
                <div>
                  <dt>Operation</dt>
                  <dd className={proposal.operation === 1 ? "danger-text" : "mono"}>
                    {proposal.operation === 0 ? "Call (0)" : "DelegateCall (1) — elevated risk"}
                  </dd>
                </div>
                <div><dt>Raw value (wei)</dt><dd className="mono break-value">{proposal.value}</dd></div>
                <div><dt>Proposed by</dt><dd><AddressView address={proposal.createdBy} explorerUrl={config.explorerUrl} /></dd></div>
                <div><dt>Safe nonce</dt><dd className="mono">{proposal.nonce}</dd></div>
              </dl>
              <details className="calldata-details">
                <summary>Review raw calldata and gas/refund fields</summary>
                <dl className="raw-fields">
                  <div><dt>to</dt><dd><AddressView address={proposal.to} explorerUrl={config.explorerUrl} full /></dd></div>
                  <div><dt>data</dt><dd><code>{proposal.data}</code></dd></div>
                  <div><dt>safeTxGas</dt><dd><code>{proposal.safeTxGas}</code></dd></div>
                  <div><dt>baseGas</dt><dd><code>{proposal.baseGas}</code></dd></div>
                  <div><dt>gasPrice</dt><dd><code>{proposal.gasPrice}</code></dd></div>
                  <div><dt>gasToken</dt><dd><AddressView address={proposal.gasToken} explorerUrl={config.explorerUrl} /></dd></div>
                  <div><dt>refundReceiver</dt><dd><AddressView address={proposal.refundReceiver} explorerUrl={config.explorerUrl} /></dd></div>
                </dl>
              </details>
              <div className="hash-callout compact"><span>Safe transaction hash</span><HashView value={proposal.safeTxHash} /></div>
              <div className="signature-grid">
                <div>
                  <span className="detail-label">Signed</span>
                  {valid.length ? valid.map((item) => <AddressView key={item.recoveredSigner} address={item.recoveredSigner} explorerUrl={config.explorerUrl} />) : <span className="muted">No signatures yet</span>}
                </div>
                <div>
                  <span className="detail-label">Missing</span>
                  {missing.length ? missing.map((owner) => <AddressView key={owner} address={owner} explorerUrl={config.explorerUrl} />) : <span className="ready-text">Threshold complete</span>}
                </div>
              </div>
              <div className="button-row proposal-actions">
                <button className="secondary-button" onClick={() => void sign(proposal)} disabled={!session || wrongNetwork || accountSigned || busyId === proposal.id}>
                  {busyId === proposal.id ? <SpinnerLabel>Confirming</SpinnerLabel> : accountSigned ? "Already signed" : "Review & sign"}
                </button>
                <button className="primary-button" onClick={() => void execute(proposal)} disabled={!session || wrongNetwork || !ready || BigInt(proposal.nonce) !== snapshot.nonce || busyId === proposal.id}>
                  {busyId === proposal.id ? <SpinnerLabel>Submitting</SpinnerLabel> : "Execute"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

export default App;
